// The projection of the home page's iPad chart of Gallivant's anchorage (TOM-980).
// Web Mercator with 256-pixel tiles at a fixed zoom, shifted so the anchor sits at
// a fixed point of a 520 x 590 viewBox. One unit is one pixel at that zoom.
import { GALLIVANT_ANCHOR, type LatLon } from './gallivant';

export const CHART_WIDTH = 520;
export const CHART_HEIGHT = 590;
export const CHART_ZOOM = 16.45;
export const CHART_TILE_SIZE = 256;
/** Where the anchor sits in the viewBox. */
export const CHART_ANCHOR_X = 245;
export const CHART_ANCHOR_Y = 255;

/** WGS 84 equatorial radius, the sphere Web Mercator uses. */
const EARTH_RADIUS_M = 6_378_137;
const WORLD_UNITS = CHART_TILE_SIZE * 2 ** CHART_ZOOM;

/** A position in Web Mercator units at CHART_ZOOM, origin at 180° W, 85.05° N. */
function toWorld({ lat, lon }: LatLon): { x: number; y: number } {
  const phi = (lat * Math.PI) / 180;
  return {
    x: ((lon + 180) / 360) * WORLD_UNITS,
    y: ((1 - Math.log(Math.tan(phi) + 1 / Math.cos(phi)) / Math.PI) / 2) * WORLD_UNITS,
  };
}

const ANCHOR_WORLD = toWorld(GALLIVANT_ANCHOR);

/** A position in the chart's viewBox; x grows east, y grows south. */
export function projectToChart(p: LatLon): { x: number; y: number } {
  const w = toWorld(p);
  return { x: w.x - ANCHOR_WORLD.x + CHART_ANCHOR_X, y: w.y - ANCHOR_WORLD.y + CHART_ANCHOR_Y };
}

/** The inverse of projectToChart. */
export function chartToLatLon({ x, y }: { x: number; y: number }): LatLon {
  const wx = x - CHART_ANCHOR_X + ANCHOR_WORLD.x;
  const wy = y - CHART_ANCHOR_Y + ANCHOR_WORLD.y;
  const lon = (wx / WORLD_UNITS) * 360 - 180;
  const lat = (Math.atan(Math.sinh(Math.PI * (1 - (2 * wy) / WORLD_UNITS))) * 180) / Math.PI;
  return { lat, lon };
}

/** Ground metres per chart unit at the anchor's latitude. */
export const CHART_M_PER_UNIT =
  ((2 * Math.PI * EARTH_RADIUS_M) / WORLD_UNITS) * Math.cos((GALLIVANT_ANCHOR.lat * Math.PI) / 180);

/** A ground distance in metres as chart units. */
export function metresToChartUnits(m: number): number {
  return m / CHART_M_PER_UNIT;
}
